import type { TreeNode } from '../types/findingHierarchy';
import { isSubsequence } from './textHelpers';

/**
 * Check if a node's value matches the query.
 * The full query (spaces stripped) must appear as a subsequence in the value.
 * e.g. "tri" matches "Tricuspid" (t→r→i in order with gaps).
 */
const matchesQuery = (node: TreeNode, qChars: string): boolean =>
  isSubsequence(qChars, node.value.toLowerCase());

/**
 * Prune tree to only branches containing matches.
 * A leaf is kept if it matches the query.
 * A folder is kept if any descendant matches (showing parent context).
 * Original ordering is maintained.
 */
const pruneTree = (nodes: TreeNode[], qChars: string): TreeNode[] => {
  const result: TreeNode[] = [];

  nodes.forEach((node) => {
    if (node.isLeaf) {
      if (matchesQuery(node, qChars)) {
        result.push(node);
      }
    } else {
      // Folder: keep if any children match
      const prunedChildren = pruneTree(node.children, qChars);
      if (prunedChildren.length > 0) {
        result.push({ ...node, children: prunedChildren });
      }
      // Also include the folder itself if its name matches (even without children)
      else if (matchesQuery(node, qChars)) {
        result.push(node);
      }
    }
  });

  return result;
};

export const filterAndScoreOptions = (
  nodes: TreeNode[],
  query: string
): TreeNode[] => {
  const q = query.toLowerCase().trim();

  if (!q) {
    // No query — preserve the incoming order. Nodes arrive already sorted by
    // `index` (see treeBuilder), i.e. the worksheet's configured order;
    // re-sorting here would clobber it.
    return nodes;
  }

  const qChars = q.replace(/\s+/g, '');

  // Prune tree to only branches containing subsequence matches
  return pruneTree(nodes, qChars);
};
